import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import productService from '../services/productService';
import cacheService from '../services/cacheService';

const Settings = () => {
  const { t } = useTranslation();
  const [cacheStats, setCacheStats] = useState(null);
  const [clearingCache, setClearingCache] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadCacheStats();
  }, []);

  const loadCacheStats = async () => {
    try {
      const stats = await productService.getCacheStats();
      setCacheStats(stats);
    } catch (error) {
      console.error('Error loading cache stats:', error);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    setMessage('');
    
    try {
      await productService.clearCache();
      setMessage(t('settings.cache.cleared_success'));
      await loadCacheStats();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error clearing cache:', error);
      setMessage(t('settings.cache.clear_error'));
    } finally {
      setClearingCache(false);
    }
  };

  const handleClearProductCache = async () => {
    setClearingCache(true);
    setMessage('');
    
    try {
      await productService.clearProductCache();
      setMessage(t('settings.cache.products_cleared_success'));
      await loadCacheStats();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error clearing product cache:', error);
      setMessage(t('settings.cache.clear_error'));
    } finally {
      setClearingCache(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-light text-bmb-text mb-2">{t('settings.title')}</h1>
          <p className="text-gray-600">{t('settings.subtitle')}</p>
        </div>

        {/* Cache Management Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-medium text-bmb-text mb-4">{t('settings.cache.title')}</h2>
          <p className="text-gray-600 mb-6">{t('settings.cache.description')}</p>

          {/* Cache Statistics */}
          {cacheStats && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">{t('settings.cache.statistics')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{t('settings.cache.memory_items')}</p>
                  <p className="text-lg font-medium text-bmb-text">{cacheStats.memoryCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('settings.cache.storage_items')}</p>
                  <p className="text-lg font-medium text-bmb-text">{cacheStats.storageCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('settings.cache.storage_size')}</p>
                  <p className="text-lg font-medium text-bmb-text">{formatBytes(cacheStats.storageSize)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success/Error Message */}
          {message && (
            <div className={`mb-4 p-3 rounded-lg ${
              message.includes('success') 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {message}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleClearProductCache}
              disabled={clearingCache}
              className="px-6 py-2 bg-bmb-text text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {clearingCache ? t('settings.cache.clearing') : t('settings.cache.clear_products')}
            </button>
            
            <button
              onClick={handleClearCache}
              disabled={clearingCache}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {clearingCache ? t('settings.cache.clearing') : t('settings.cache.clear_all')}
            </button>

            <button
              onClick={loadCacheStats}
              disabled={clearingCache}
              className="px-6 py-2 border border-bmb-text text-bmb-text rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('settings.cache.refresh_stats')}
            </button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>{t('settings.cache.note')}:</strong> {t('settings.cache.note_description')}
            </p>
          </div>
        </div>

        {/* API Configuration Info */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-medium text-bmb-text mb-4">{t('settings.api.title')}</h2>
          <div className="space-y-2">
            <div className="flex items-center">
              <span className="text-sm text-gray-600 w-32">{t('settings.api.endpoint')}:</span>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                {process.env.REACT_APP_API_URL || 'https://bmbjewels.com/api/catalog'}
              </code>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 w-32">{t('settings.api.status')}:</span>
              <span className="text-sm text-green-600 font-medium">{t('settings.api.connected')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;